import { QueryClient } from '@tanstack/react-query';

// Create a client with sensible defaults for our app
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30 seconds stale time - good balance between freshness and performance
      staleTime: 30 * 1000,
      // 5 minutes garbage collection time
      gcTime: 5 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors except 408/429
        if (
          error?.status >= 400 &&
          error?.status < 500 &&
          error?.status !== 408 &&
          error?.status !== 429
        ) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Exponential backoff for retries
      retryDelay: attemptIndex =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (helps keep data fresh)
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect if data is fresh
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once (they're usually not safe to retry)
      retry: (failureCount, error: any) => {
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

// Export query keys for consistency across the app
export const queryKeys = {
  // Realm queries
  realm: (realmId: string) => ['realm', realmId] as const,
  realmQuests: (realmId: string) =>
    ['realm', realmId, 'quests'] as const,
  realmLeaderboard: (realmId: string) =>
    ['realm', realmId, 'leaderboard'] as const,

  // Hero queries
  hero: (heroId: string) => ['hero', heroId] as const,
  heroAvatar: (heroId: string) =>
    ['hero', heroId, 'avatar'] as const,
  heroEquipment: (heroId: string) =>
    ['hero', heroId, 'equipment'] as const,

  // User queries
  me: ['me'] as const,
  myCoins: ['me', 'coins'] as const,

  // Inventory queries
  inventory: (heroId: string) =>
    ['inventory', heroId] as const,

  // Chamber queries
  chamber: ['hero', 'chamber'] as const,

  // Personal quest queries
  personalQuests: ['hero', 'personal-quests'] as const,
} as const;
